import type { DungeonTemplateId, Item, JobDefinition } from '@renavora/shared';

import type { SerializableCharacter } from '../persistence/character';

export interface JobUnlockEvaluation {
  readonly unlocked: boolean;
  readonly missing: readonly string[];
}

function maxKnownJobLevel(character: SerializableCharacter, jobId: string): number {
  const currentLevel = character.jobId === jobId ? character.jobLevel : 0;
  const historyLevel = character.jobHistory.find((entry) => entry.jobId === jobId)?.maxLv ?? 0;
  return Math.max(currentLevel, historyLevel);
}

function hasRequiredItem(inventory: ReadonlyArray<Item>, itemId: string): boolean {
  return inventory.some((item) => item.id === itemId || item.baseId === itemId);
}

function hasClearedDungeon(
  completedDungeons: ReadonlyArray<DungeonTemplateId>,
  dungeonId: string,
): boolean {
  return completedDungeons.some((id) => id === dungeonId);
}

function isVisited(character: SerializableCharacter, jobId: string): boolean {
  return character.jobId === jobId || character.jobHistory.some((entry) => entry.jobId === jobId);
}

export function evaluateJobUnlock(
  character: SerializableCharacter,
  job: JobDefinition,
): JobUnlockEvaluation {
  if (job.tier === 1 || isVisited(character, job.id)) {
    return { unlocked: true, missing: [] };
  }

  const unlock = job.unlock;
  if (unlock === undefined) {
    return { unlocked: false, missing: ['unlock data'] };
  }

  const missing: string[] = [];
  for (const [jobId, requiredLevel] of Object.entries(unlock.requiredJobMaxLevels ?? {})) {
    const knownLevel = maxKnownJobLevel(character, jobId);
    if (knownLevel < requiredLevel) {
      missing.push(`${jobId} Lv ${requiredLevel}`);
    }
  }

  for (const itemId of unlock.requiredItems ?? []) {
    if (!hasRequiredItem(character.inventory, itemId)) {
      missing.push(`item ${itemId}`);
    }
  }

  for (const dungeonId of unlock.requiredDungeonClears ?? []) {
    if (!hasClearedDungeon(character.completedDungeons, dungeonId)) {
      missing.push(`clear ${dungeonId}`);
    }
  }

  if ((unlock.requiredBossKills?.length ?? 0) > 0) {
    missing.push('boss kill history');
  }
  if ((unlock.requiredQuests?.length ?? 0) > 0) {
    missing.push('quest history');
  }

  return { unlocked: missing.length === 0, missing };
}
